import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";

import { cn } from "@/lib/utils";

const Drawer = ({
  shouldScaleBackground = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    dismissible={process.env.NODE_ENV === "test" ? false : true}
    shouldScaleBackground={shouldScaleBackground}
    repositionInputs={false}
    {...props}
  />
);
Drawer.displayName = "Drawer";

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 z-50 bg-[#00778B]/80", className)}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DrawerPortal>
    <DrawerOverlay />
    <DrawerPrimitive.Content
      ref={ref}
      className={cn(
        "fixed inset-x-0 bottom-0 z-50 mt-24 flex h-full max-h-[95%] flex-col overflow-hidden rounded-t-xl bg-[#041935] after:!h-auto",
        className
      )}
      {...props}
    >
      <div className="h-full overflow-y-auto p-5">{children}</div>
    </DrawerPrimitive.Content>
  </DrawerPortal>
));
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({
  title,
  leftButton,
  rightButton,
  className,
  children,
  description,
  ...props
}: {
  title?: string;
  leftButton?: React.ReactNode;
  rightButton?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
  description?: string;
}) => {
  if (children) {
    return (
      <div {...props} className={cn("mb-10", className)}>
        {children}
      </div>
    );
  }

  return (
    <div
      {...props}
      className={cn("mb-5 flex flex-row justify-between gap-1", className)}
    >
      <div className="flex items-start">{leftButton}</div>

      <div className="relative flex flex-col text-nowrap text-center text-[15px] font-medium leading-5 text-white">
        {title}
        <span
          style={{
            filter: "blur(35px)",
            WebkitFilter: "blur(35px)",
            WebkitBackdropFilter: "blur(35px)",
          }}
          className="absolute left-1/2 top-[-15px] z-20 h-[50px] w-[200px] -translate-x-1/2 rounded-full bg-[#26C2DD54]"
        ></span>
        {description && (
          <span className="mt-2 max-w-[380px] text-wrap text-center text-sm text-muted-foreground">
            {description}
          </span>
        )}
      </div>

      <div className="flex items-start">{rightButton}</div>
    </div>
  );
};
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("mt-auto flex flex-col gap-2 p-4", className)}
    {...props}
  />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
